import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { createQueryClient } from './lib/queryClient';
import { AuthProvider } from './auth/AuthProvider';
import { LoginPage } from './auth/LoginPage';
import { GuestOnly, MFA_SETUP_PATH, RequireAuth } from './auth/RequireAuth';
import { createAppTheme } from './design/theme';
import { featureRoutes } from './features';
import { ForcedMfaSetupPage } from './features/account/ForcedMfaSetupPage';
import { AppShell } from './layout/AppShell';
import { ComingSoonPage } from './pages/ComingSoonPage';

const theme = createAppTheme();

/** Routes: /login (guests), /setup-mfa (forced enrolment), everything else inside the shell. */
export function AppRoutes() {
  const hasIndex = featureRoutes.some((r) => r.path === '');
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <GuestOnly>
            <LoginPage />
          </GuestOnly>
        }
      />
      <Route
        path={MFA_SETUP_PATH}
        element={
          <RequireAuth>
            <ForcedMfaSetupPage />
          </RequireAuth>
        }
      />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {!hasIndex && <Route index element={<ComingSoonPage />} />}
        {featureRoutes.map((r) =>
          r.path === '' ? (
            <Route key="index" index element={r.element} />
          ) : (
            <Route key={r.path} path={r.path} element={r.element} />
          ),
        )}
        <Route path="*" element={<ComingSoonPage />} />
      </Route>
    </Routes>
  );
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  return (
    <ThemeProvider
      theme={theme}
      defaultMode="light"
      modeStorageKey="sm.admin.mode"
      disableTransitionOnChange
    >
      <CssBaseline enableColorScheme />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </SnackbarProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
